import { useState } from 'react';
import { CalendarMonthWeekGrid } from '~/features/shared/calendar/CalendarMonthWeekGrid';
import { CalendarEvent, CalendarViewMode } from '../types';
import { buildMonthGrid, buildWeekDays, formatDisplayDate, getStatusLevel } from '../utils';
import { EventBar } from './EventBar';

interface CalendarGridProps {
  anchor: Date;
  viewMode: CalendarViewMode;
  eventsByDate: Map<string, CalendarEvent[]>;
  onSelectEvent: (event: CalendarEvent) => void;
}

interface DayContentsProps {
  iso: string;
  events: CalendarEvent[];
  isMonth: boolean;
  maxVisible: number;
  today: Date;
  onSelectEvent: (event: CalendarEvent) => void;
}

const DayContents = ({
  iso,
  events,
  isMonth,
  maxVisible,
  today,
  onSelectEvent,
}: DayContentsProps) => {
  const [expanded, setExpanded] = useState(false);
  const overflow = events.length - maxVisible;
  const visible = expanded ? events : events.slice(0, maxVisible);

  return (
    <div className="flex flex-col gap-0.5">
      {visible.map((event) => (
        <EventBar
          compact={isMonth}
          event={event}
          key={event.eventId}
          onClick={onSelectEvent}
          statusLevel={getStatusLevel(event, today)}
        />
      ))}
      {overflow > 0 && (
        <button
          aria-expanded={expanded}
          aria-label={
            expanded
              ? `Show fewer events on ${formatDisplayDate(iso)}`
              : `Show ${overflow} more events on ${formatDisplayDate(iso)}`
          }
          className="cursor-pointer rounded px-1 text-left text-[11px] font-semibold text-primary-50 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-50/40"
          onClick={() => setExpanded((value) => !value)}
          type="button"
        >
          {expanded ? 'Show less' : `+${overflow} more`}
        </button>
      )}
    </div>
  );
};

export const CalendarGrid = ({
  anchor,
  viewMode,
  eventsByDate,
  onSelectEvent,
}: CalendarGridProps) => {
  const today = new Date();
  const days = viewMode === 'month' ? buildMonthGrid(anchor) : buildWeekDays(anchor);
  const isMonth = viewMode === 'month';
  const maxVisible = isMonth ? 3 : 12;

  return (
    <CalendarMonthWeekGrid
      anchor={anchor}
      dates={days}
      renderDay={({ isoDate }) => (
        <DayContents
          events={eventsByDate.get(isoDate) ?? []}
          isMonth={isMonth}
          iso={isoDate}
          maxVisible={maxVisible}
          onSelectEvent={onSelectEvent}
          today={today}
        />
      )}
      today={today}
      viewMode={viewMode}
    />
  );
};
